import React, { useEffect, useState } from 'react';
import Autocomplete, {
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import { SelectOption, Option } from '@im/ui-lib/SelectOption';
import IconButton from '@mui/material/IconButton';
import { useTranslate } from '@im/react-localization';
import CloseIcon from '@mui/icons-material/Close';
import { SxProps, styled } from '@mui/material/styles';
import Box from '@mui/material/Box';

type ValueType = {
  endDate?: Date | null;
  startDate?: Date | null;
};

export type Props = {
  autoCompleteProps?: any;
  dateFormat?: (sDate: Date | null, eDate: Date | null) => string;
  defaultDropdownOptionSelected?: number;
  dividers?: Array<number>;
  dropdownMenuSx?: SxProps;
  dropdownOptions?: Option[];
  intervals?: any;
  maxDate?: Date | null;
  minDate?: Date | null;
  onClearClick?: (option?: string, value1?: any, value2?: any) => any;
  onDateChange?: (sDate: Date | null, eDate: Date | null) => any;
  onDropdownOptionChange?: (option?: Option) => any;
  onIntervalSelected?: (obj: any) => void;
  onSearch: (value: { dropdownTerm: string; inputValue: string }) => void;
  selectedDropdownOptionIndex?: number;
  value?: ValueType | string | null;
} & TextFieldProps;

export function SearchBar(props: Props) {
  const {
    autoCompleteProps: autoComplete,
    dateFormat,
    defaultDropdownOptionSelected = 0,
    dividers,
    dropdownMenuSx,
    dropdownOptions,
    intervals,
    maxDate,
    minDate,
    onClearClick,
    onDateChange,
    onDropdownOptionChange,
    onIntervalSelected,
    onSearch,
    selectedDropdownOptionIndex = 0,
    value = null,
    ...others
  } = props;
  const [dropdownTerm, setDropdownTerm] = useState(
    dropdownOptions ? dropdownOptions[0].value : ''
  );
  const [inputValue, setInputValue] = useState('');
  const translate = useTranslate();

  const onClose = () => {
    setInputValue('');
  };

  const onKeyPress = async (event: React.KeyboardEvent) => {
    onSearch({ dropdownTerm, inputValue });
  };

  useEffect(() => {
    if (dropdownOptions) {
      const val = dropdownOptions[selectedDropdownOptionIndex];
      setDropdownTerm(val?.value);
    }
  }, [dropdownOptions, selectedDropdownOptionIndex]);

  useEffect(() => {
    if (typeof value === 'string') {
      setInputValue(value);
    }
  }, [value]);

  function handleInputChange(
    _: any,
    value: string,
    reason: AutocompleteInputChangeReason
  ) {
    if (reason !== 'reset') {
      setInputValue(value);
    }
  }

  const autoCompleteProps = {
    ...autoComplete,
    inputValue,
    onInputChange: handleInputChange,
  };

  const renderInput = ({
    InputProps: { endAdornment, startAdornment, ...inputProps } = {} as any,
    ...props
  }: any) => (
    <StyledTextField
      InputProps={{
        endAdornment: inputValue && (
          <IconButton
            aria-label={translate('Dashboard.Delete')}
            onClick={() => {
              onClose();
              if (onClearClick) {
                if (dropdownOptions) {
                  const val = dropdownOptions[defaultDropdownOptionSelected];
                  setDropdownTerm(val?.value);
                  if (onDropdownOptionChange) {
                    onDropdownOptionChange(val);
                  }
                  setInputValue('');
                }
                onClearClick('clear_text', { dropdownTerm, inputValue });
              }
            }}
            onMouseDown={(e) => e.preventDefault()}
            sx={{
              '& .MuiSvgIcon-root': {
                height: '24px',
                width: '24px',
              },
              visibility: 'visible',
            }}
          >
            <CloseIcon />
          </IconButton>
        ),
        startAdornment: (
          <InputAdornment position="start">
            {dropdownOptions ? (
              <StyleStack
                alignItems="center"
                direction="row"
                justifyContent="center"
              >
                <StyledBox>
                  <SelectOption
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiList-root': {
                            padding: '8px 12px',
                          },
                          '& .MuiMenuItem-root': {
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '22px',
                            padding: '5px 8px',
                          },
                          borderRadius: '4px',
                          boxShadow: '0px 2px 6px rgba(51, 51, 51, 0.15)',
                          minWidth: '140px !important',
                          ...dropdownMenuSx,
                        },
                      },
                    }}
                    data-testid="search-dropdown"
                    disableUnderline
                    dividers={dividers}
                    onChange={(e) => {
                      setDropdownTerm(e.target.value as string);
                      const val = dropdownOptions.find(
                        (i) => i.value === e.target.value
                      );
                      if (onDropdownOptionChange) {
                        onDropdownOptionChange(val);
                      }
                    }}
                    options={dropdownOptions}
                    sx={{
                      '& .MuiSvgIcon-root': {
                        height: '18px !important',
                        top: '6px',
                        width: '18px !important',
                      },
                    }}
                    value={dropdownTerm}
                    variant="standard"
                  />
                </StyledBox>
                <IconButton
                  aria-label={translate('Common.Search')}
                  onClick={() => onSearch({ dropdownTerm, inputValue })}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      height: '24px !important',
                      width: '24px !important',
                    },
                    marginLeft: '8px',
                  }}
                >
                  <SearchIcon className="searchIcon" />
                </IconButton>
              </StyleStack>
            ) : (
              <IconButton
                aria-label={translate('Common.Search')}
                onClick={() => onSearch({ dropdownTerm, inputValue })}
                sx={{
                  '& .MuiSvgIcon-root': {
                    height: '24px !important',
                    width: '24px !important',
                  },
                }}
              >
                <SearchIcon className="searchIcon" />
              </IconButton>
            )}
          </InputAdornment>
        ),
        ...inputProps,
      }}
      autoComplete="off"
      onChange={(e) => setInputValue(e.target.value)}
      onKeyUp={onKeyPress}
      value={inputValue}
      {...props}
    />
  );

  return autoComplete ? (
    <StyledAutocomplete
      autoHighlight={true}
      disablePortal
      freeSolo
      {...autoCompleteProps}
      renderInput={(props) => renderInput({ ...others, ...props })}
    />
  ) : (
    renderInput(others)
  );
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiIconButton-root': {
    height: '24px',
    padding: '0px',
    width: '24px',
  },
  '& .MuiOutlinedInput-input': {
    color: theme.palette.grey[600],
    padding: '6px 0',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: `1px solid ${theme.palette.grey[600]}`,
      borderRadius: '4px',
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.palette.primary[700]} !important`,
      },
      '& .searchIcon': {
        color: theme.palette.primary[700],
      },
      boxShadow: 'unset',
    },
    '&:has(.MuiInputAdornment-positionStart)': {
      paddingLeft: '12px',
      paddingRight: '12px',
    },
    fontSize: '14px',
    fontWeight: '400',
    height: '32px',
    lineHeight: '22px',
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.grey[600],
  },
  width: '100%',
}));

const StyleStack = styled(Stack)(({ theme }) => ({
  '& .MuiInput-root': {
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '22px',
    minWidth: '120px',
  },
  '& .MuiSelect-select': {
    alignItems: 'center',
    display: 'flex',
    height: '32px',
    padding: 0,
  },
}));

const StyledBox = styled(Box)(({ theme }) => ({
  borderRight: `1px solid rgba(107, 107, 107, 0.7)`,
  paddingRight: '8px',
}));

const StyledAutocomplete = styled(Autocomplete)(() => ({
  '& .MuiInputAdornment-root': {
    marginRight: 0,
  },
  '& .MuiOutlinedInput-root': {
    '& .MuiInput-root .MuiInput-input': {
      padding: '0px 24px 1px 0 !important',
    },
    borderRadius: 50,
    paddingLeft: 2,
  },
}));
